import { useMemo, useState } from 'react';
import { Button, Empty, Input, Modal, Popover, Space, Tooltip, Typography } from 'antd';
import { CloseOutlined, SearchOutlined, AppstoreOutlined } from '@ant-design/icons';
import { ICON_CATEGORIES, acqRenderIcon } from './IconRegistry';

const { Text } = Typography;

interface IconPickerProps {
  acqValue?: string | null;
  acqOnChange: (acqKey: string | null) => void;
  acqDisabled?: boolean;
  acqSize?: 'small' | 'middle' | 'large';
  /**
   * `popover` — компактна кнопка з popover-grid (default, для inline-форм).
   * `modal` — велика модалка, краще для багатьох іконок (можна перемикнути).
   */
  acqMode?: 'popover' | 'modal';
}

/**
 * Picker для AntD-іконки з курованого реєстру (~60 іконок, 10 категорій).
 * Зберігає рядок-ключ (наприклад `"DatabaseOutlined"`) у полі `iconKey`,
 * який потім рендериться через `acqRenderIcon(key)` у меню/списках.
 */
export default function IconPicker({
  acqValue, acqOnChange, acqDisabled, acqSize = 'middle', acqMode = 'popover',
}: IconPickerProps) {
  const [acqOpen, setAcqOpen] = useState(false);

  const acqHandlePick = (acqKey: string) => {
    acqOnChange(acqKey);
    setAcqOpen(false);
  };

  const acqClear = () => acqOnChange(null);

  const acqTrigger = (
    <Space.Compact>
      <Button
        size={acqSize}
        disabled={acqDisabled}
        icon={acqValue ? acqRenderIcon(acqValue) : <AppstoreOutlined />}
        onClick={() => setAcqOpen(true)}
        style={{ minWidth: 140 }}
      >
        {acqValue ?? 'Обрати іконку…'}
      </Button>
      {acqValue && (
        <Tooltip title="Очистити">
          <Button
            size={acqSize}
            disabled={acqDisabled}
            icon={<CloseOutlined />}
            onClick={acqClear}
          />
        </Tooltip>
      )}
    </Space.Compact>
  );

  if (acqMode === 'modal') {
    return (
      <>
        {acqTrigger}
        <Modal
          title="Обрати іконку"
          open={acqOpen}
          onCancel={() => setAcqOpen(false)}
          footer={null}
          width={720}
          destroyOnClose
        >
          <IconGrid acqValue={acqValue ?? null} acqOnPick={acqHandlePick} />
        </Modal>
      </>
    );
  }

  // popover-mode
  return (
    <Popover
      open={acqOpen}
      onOpenChange={setAcqOpen}
      trigger="click"
      placement="bottomLeft"
      content={
        <div style={{ width: 480 }}>
          <IconGrid acqValue={acqValue ?? null} acqOnPick={acqHandlePick} />
        </div>
      }
      destroyTooltipOnHide
    >
      {acqTrigger}
    </Popover>
  );
}

/**
 * Grid іконок з пошуком + категоріями. Показуються всі категорії; пошук
 * фільтрує одночасно по ключу і по label.
 */
function IconGrid({
  acqValue, acqOnPick,
}: {
  acqValue: string | null;
  acqOnPick: (acqKey: string) => void;
}) {
  const [acqQuery, setAcqQuery] = useState('');

  const acqFiltered = useMemo(() => {
    const acqQ = acqQuery.trim().toLowerCase();
    if (!acqQ) return ICON_CATEGORIES;
    return ICON_CATEGORIES
      .map((acqCat) => ({
        ...acqCat,
        icons: acqCat.icons.filter((acqI) =>
          acqI.key.toLowerCase().includes(acqQ) || acqI.label.toLowerCase().includes(acqQ),
        ),
      }))
      .filter((acqCat) => acqCat.icons.length > 0);
  }, [acqQuery]);

  return (
    <div>
      <Input
        size="small"
        placeholder="Пошук…"
        prefix={<SearchOutlined />}
        value={acqQuery}
        onChange={(e) => setAcqQuery(e.target.value)}
        autoFocus
        allowClear
        style={{ marginBottom: 8 }}
      />
      <div style={{ maxHeight: 380, overflowY: 'auto' }}>
        {acqFiltered.length === 0 ? (
          <Empty description="Нічого не знайдено" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          acqFiltered.map((acqCat) => (
            <div key={acqCat.label} style={{ marginBottom: 8 }}>
              <Text type="secondary" style={{ fontSize: 11, textTransform: 'uppercase' }}>
                {acqCat.label}
              </Text>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(8, 1fr)',
                gap: 4,
                marginTop: 4,
              }}>
                {acqCat.icons.map((acqI) => (
                  <Tooltip key={acqI.key} title={`${acqI.label} (${acqI.key})`}>
                    <div
                      onClick={() => acqOnPick(acqI.key)}
                      style={{
                        cursor: 'pointer',
                        padding: 8,
                        textAlign: 'center',
                        fontSize: 18,
                        borderRadius: 4,
                        background: acqValue === acqI.key ? '#e6f4ff' : 'transparent',
                        border: `1px solid ${acqValue === acqI.key ? '#1677ff' : '#f0f0f0'}`,
                        transition: 'all 0.15s',
                      }}
                      onMouseEnter={(e) => {
                        if (acqValue !== acqI.key) (e.currentTarget as HTMLElement).style.background = '#fafafa';
                      }}
                      onMouseLeave={(e) => {
                        if (acqValue !== acqI.key) (e.currentTarget as HTMLElement).style.background = 'transparent';
                      }}
                    >
                      {acqRenderIcon(acqI.key)}
                    </div>
                  </Tooltip>
                ))}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
